import { useState } from "react";
import styles from "./FirmLogoCopy.module.css";

type LogoThumbnailProps = {
  url: string | null;
  alt: string;
};

type Dimensions = {
  width: number;
  height: number;
};

function isWebAddress(url: string): boolean {
  if (!URL.canParse(url)) {
    return false;
  }

  const { protocol } = new URL(url);

  return protocol === "https:" || protocol === "http:";
}

function isVector(url: string): boolean {
  return new URL(url).pathname.toLowerCase().endsWith(".svg");
}

function sizeLabelOf(url: string, dimensions: Dimensions): string {
  if (isVector(url)) {
    return "Vector (any size)";
  }

  return `${dimensions.width} × ${dimensions.height} px`;
}

export function LogoThumbnail({ url, alt }: LogoThumbnailProps) {
  const [hasFailed, setHasFailed] = useState(false);
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);

  if (!url) {
    return <span className={styles.none}>None</span>;
  }

  if (!isWebAddress(url)) {
    return <span className={styles.none}>Not a web address, so not shown</span>;
  }

  return (
    <div>
      <a
        className={styles.thumbnailLink}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
      >
        {hasFailed ? (
          <span
            className={styles.thumbnailFailed}
            role="img"
            aria-label={`${alt}: image failed to load`}
          >
            Image failed to load
          </span>
        ) : (
          <img
            className={styles.thumbnail}
            src={url}
            alt={alt}
            loading="lazy"
            onLoad={event =>
              setDimensions({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            onError={() => setHasFailed(true)}
          />
        )}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>

      {hasFailed || dimensions === null ? null : (
        <div className={styles.meta}>{sizeLabelOf(url, dimensions)}</div>
      )}
    </div>
  );
}
